import type { PatientDrawerResponse } from '../../api/generated';
import { useTranslation } from 'react-i18next';
import { PatientDetailsCard } from '../PatientDetails/PatientDetailsCard';
import { AuditLog } from '../PatientDetails/AuditLog';
import { PatientTreatmentsList } from '../PatientTreatmentsList/PatientTreatmentsList';
import { LoadError } from '../UI/LoadError';
import { LoadingPlaceholder } from '../UI/LoadingPlaceholder';
import styles from './PatientDetailsDrawer.module.css';

interface PatientDetailsViewBodyProps {
  locationId: string;
  details: PatientDrawerResponse | null;
  isRefreshing: boolean;
  hasDetailsError: boolean;
  isEditing: boolean;
  isEditSliding: boolean;
  canEdit: boolean;
  hasRoomPropertiesError: boolean;
  onRetryDetails: () => void;
  onRetryRoomProperties: () => void;
  onEditStart: () => void;
  onEditEnd: () => void;
  onPatientUpdated: () => void;
}

export const PatientDetailsViewBody = ({
  locationId,
  details,
  isRefreshing,
  hasDetailsError,
  isEditing,
  isEditSliding,
  canEdit,
  hasRoomPropertiesError,
  onRetryDetails,
  onRetryRoomProperties,
  onEditStart,
  onEditEnd,
  onPatientUpdated
}: PatientDetailsViewBodyProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.content}>
      {/* These three are mutually exclusive and jointly exhaustive: a
          failed fetch offers a way out, an in-flight one shows the
          placeholder, and otherwise the cards render. Without the error
          branch the placeholder would be permanent — the drawer never
          unmounts, so nothing else would ever retry. */}
      {!details && hasDetailsError && (
        <LoadError message={t('patient-details-load-error')} onRetry={onRetryDetails} />
      )}
      {!details && !hasDetailsError && <LoadingPlaceholder />}
      {details && (
        <div className={styles.slideContainer}>
          <div className={`${styles.mainView} ${isEditing ? styles.slideOut : ''}`}>
            {/* Only this slot holds values a refresh can invalidate.
                Shimmering it alone keeps the header, the slide
                container and the edit card — and its form state —
                mounted, so a save still slides out normally. */}
            {isRefreshing ? (
              <LoadingPlaceholder />
            ) : (
              <>
                <PatientDetailsCard
                  patient={details.profile}
                  locationId={locationId}
                  mode="view"
                  onEditStart={onEditStart}
                  onEditEnd={onEditEnd}
                  onPatientUpdated={onPatientUpdated}
                />

                <PatientTreatmentsList treatments={details.treatmentPlans} />

                <AuditLog entries={details.auditLog} />
              </>
            )}
          </div>

          {isEditing && (
            <div className={`${styles.editView} ${isEditSliding ? styles.slideIn : ''}`}>
              {/* The one place room needs can be destroyed: seeding
                  this form from an unloaded table would send an empty
                  list on save. Latched, so it cannot be pulled out from
                  under an edit already in progress. */}
              {canEdit && (
                <PatientDetailsCard
                  patient={details.profile}
                  locationId={locationId}
                  mode="edit"
                  onEditStart={onEditStart}
                  onEditEnd={onEditEnd}
                  onPatientUpdated={onPatientUpdated}
                />
              )}
              {!canEdit && hasRoomPropertiesError && (
                <LoadError
                  message={t('room-properties-load-error')}
                  onRetry={onRetryRoomProperties}
                />
              )}
              {!canEdit && !hasRoomPropertiesError && <LoadingPlaceholder />}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
